import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { blogPosts } from '../data/blogPosts';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';

const BlogPage = () => {
  const featuredPost = blogPosts[0];
  const otherPosts = blogPosts.slice(1);

  return (
    <main data-testid="blog-page" className="bg-gray-50 min-h-screen">
      <SEO 
        title="Appliance Tips & Maintenance Blog | BBM Appliances"
        description="Expert appliance maintenance tips, repair guides, and troubleshooting advice. Learn how to extend the life of your washer, dryer, refrigerator & more."
        canonical="https://www.bbmappliances.com/blog"
        keywords="appliance maintenance tips, appliance repair guide, washing machine maintenance, refrigerator troubleshooting, dryer repair tips, appliance care"
      />
      
      {/* Header */}
      <div className="bg-foreground text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-8 h-8 text-primary" />
            <span className="text-primary font-medium">BBM Appliances Blog</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4">
            Appliance Tips & Guides
          </h1>
          <p className="text-white/70 text-lg max-w-2xl">
            Expert advice to help you maintain your appliances, troubleshoot common problems, 
            and know when it's time to call a professional.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Post */}
        <section data-testid="featured-post" className="mb-16">
          <h2 className="text-sm font-semibold text-primary uppercase tracking-wider mb-6">Featured Article</h2>
          <Link 
            to={`/blog/${featuredPost.slug}`}
            className="group block bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className="grid md:grid-cols-2 gap-0">
              <div className="aspect-video md:aspect-auto">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full w-fit mb-4">
                  {featuredPost.category}
                </span>
                <h3 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-4 group-hover:text-primary transition-colors">
                  {featuredPost.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{featuredPost.date}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime}
                    </span>
                  </div>
                  <span className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
                    Read Article
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </section>

        {/* Other Posts Grid */}
        <section data-testid="blog-posts-grid">
          <h2 className="text-sm font-semibold text-primary uppercase tracking-wider mb-6">More Articles</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-8">
            {otherPosts.map((post) => (
              <Link 
                key={post.id}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full mb-3">
                    {post.category}
                  </span>
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3 text-gray-500">
                      <span>{post.date}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section data-testid="blog-cta" className="mt-16 bg-primary rounded-2xl p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
            Need Professional Appliance Help?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Our expert technicians are ready to diagnose and repair your appliances. 
            Same-day appointments available in PA & NJ.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/book"
              data-testid="blog-cta-book"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-primary font-semibold rounded-full hover:bg-gray-100 transition-colors"
            >
              Book a Service
            </Link>
            <a 
              href="tel:+18556056034"
              data-testid="blog-cta-call"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-colors"
            >
              Call (855) 605-6034
            </a>
          </div>
        </section>
      </div>
    </main>
  );
};

export default BlogPage;
